import { debounce } from 'lodash';
import { redirect } from '@respond-framework/rudy';
import { pMinDelay } from '../../utilities/pMinDelay';
import { ResourceApi } from '../../../resource/Resource';
import { ActionTypes } from './actions';
import {
  selectFilteredCourses,
  selectFilters,
  selectCurrentRequestId,
  selectSearchByCourseFilters,
  selectFeaturedInstitutions,
  selectCategories,
  selectFeaturedCourses,
  selectInitialDataLoaded,
} from './selectors';
import { ROUTE_COURSES } from '../../../ReduxStorage/routeActions';
import { makeMetricsLogEvent } from '../../../ReduxStorage/Metrics/manualEventDispatchers';
import { selectQueryObject } from '../../../ReduxStorage/location/selectors.web';
import { INSTITUTION_RESOURCE } from '../../../ReduxStorage/resourceNames';

const SEARCH_DELAY = 1500;
const NUM_RESULTS = 24; // choose a nunber that supports 4 and 3 column layouts

const searchByCourse = async (args) =>
  pMinDelay(ResourceApi.lookup('search', 'byCourse', args), 400);

export const fetchSearchResults = () => (dispatch, getState) => {
  const state = getState();
  dispatch(makeMetricsLogEvent('Filter-Selected', selectFilters(state)));

  const filters = selectSearchByCourseFilters(state);
  const requestId = selectCurrentRequestId(state);
  return searchByCourse({
    start: 0,
    size: NUM_RESULTS,
    profileName: null,
    ...filters,
  }).then(
    (results) => {
      dispatch({
        type: ActionTypes.COURSE_LISTING_SEARCH_COURSES_SUCCESS,
        requestId,
        ...results,
      });
    },
    (error) => {
      dispatch({
        type: ActionTypes.COURSE_LISTING_SEARCH_COURSES_FAILURE,
        requestId,
        error: error.message,
      });
    }
  );
};

export const loadFilteredCourses = debounce(fetchSearchResults(), SEARCH_DELAY);

export const updateFilters = (changes) => {
  return (dispatch, getState) => {
    const state = getState();
    const query = selectQueryObject(state);
    const filters = selectFilters(state);
    dispatch(
      redirect({
        type: ROUTE_COURSES,
        query: {
          ...query,
          filters: JSON.stringify({
            ...filters,
            ...changes,
          }),
        },
      })
    );
  };
};

export const loadMoreResults = () => (dispatch, getState) => {
  const state = getState();
  const filters = selectSearchByCourseFilters(state);

  const requestId = selectCurrentRequestId(state);
  return searchByCourse({
    start: selectFilteredCourses(state).length,
    size: NUM_RESULTS,
    ...filters,
  }).then((results) => {
    dispatch({
      type: ActionTypes.COURSE_LISTING_LOAD_MORE_SUCCESS,
      requestId,
      ...results,
    });
  });
};

const loadFeaturedInstitutions = (dispatch, getState) => {
  const featuredInstitutions = selectFeaturedInstitutions(getState());
  if (featuredInstitutions.length === 0) {
    return ResourceApi.lookup(INSTITUTION_RESOURCE, 'getFeaturedInstitutions', {
      localised: true,
      country: 'all',
    }).then((institutions) => {
      return institutions
        .map((institution) => ({
          label: institution.name,
          value: institution.path,
        }))
        .sort((a, b) => {
          const strA = a.label.toLowerCase();
          const strB = b.label.toLowerCase();
          return strA === strB ? 0 : strA > strB ? 1 : -1;
        });
    });
  }
  return Promise.resolve(featuredInstitutions);
};

const loadFeaturedCourses = (dispatch, getState) => {
  const featuredCourses = selectFeaturedCourses(getState());
  if (featuredCourses.length === 0) {
    const query = selectQueryObject(getState());
    return ResourceApi.lookup('course', 'getFeaturedCourses', {
      country: query.country || null,
      localised: true,
    });
  }
  return Promise.resolve(featuredCourses);
};

const loadCourseCategories = (dispatch, getState) => {
  const categories = selectCategories(getState());
  if (categories.length === 0) {
    return ResourceApi.lookup(
      'course',
      'categories',
      {}
    ).then((courseCategories) => Object.values(courseCategories).sort());
  }
  return Promise.resolve(categories);
};

export const loadCourseListingData = async (dispatch, getState) => {
  const state = getState();
  const loaded = selectInitialDataLoaded(state);
  const requestId = selectCurrentRequestId(state);
  if (!loaded) {
    dispatch({
      type: ActionTypes.COURSE_LISTING_DATA_START,
      requestId,
    });
    const [
      featuredInstitutions,
      featuredCourses,
      categories,
    ] = await Promise.all([
      dispatch(loadFeaturedInstitutions),
      dispatch(loadFeaturedCourses),
      dispatch(loadCourseCategories),
    ]);
    dispatch({
      type: ActionTypes.COURSE_LISTING_DATA_LOADED,
      featuredCourses,
      featuredInstitutions,
      categories,
    });
  }
};
